import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/PhoneInput";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";

export default function Register() {
  const navigate = useNavigate();
  const { setUser, setCompany } = useAuth();
  const [plans, setPlans] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    legal_name: "",
    trade_name: "",
    cnpj: "",
    phone: "",
    name: "",
    email: "",
    password: "",
    plan_slug: "starter",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plans");
        setPlans(data);
      } catch (err) {
        console.error("Failed to load plans", err);
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register-company", form);
      setToken(data.token);
      setUser(data.user);
      const me = await api.get("/auth/me");
      setCompany(me.data.company || null);
      toast.success("Conta criada! Bem-vindo à Ultra.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao cadastrar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-3xl p-8 sm:p-10 rounded-2xl border-stone-200">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <img src="/logo.png" alt="Ultra" className="h-9 w-9 rounded-lg" />
          <span className="font-display text-xl font-extrabold">Ultra</span>
        </Link>

        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
          Crie sua conta empresarial
        </h1>
        <p className="mt-2 font-body text-sm text-stone-500">
          14 dias de trial gratuito. Sem cartão. Multi-unidade nativo.
        </p>

        <form onSubmit={submit} data-testid={TID.registerForm} className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className="font-body text-sm">Razão Social *</Label>
            <Input value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} required data-testid={TID.registerLegalName} className="mt-1.5 bg-white" />
          </div>
          <div>
            <Label className="font-body text-sm">Nome Fantasia *</Label>
            <Input value={form.trade_name} onChange={(e) => set("trade_name", e.target.value)} required data-testid={TID.registerTradeName} className="mt-1.5 bg-white" />
          </div>
          <div>
            <Label className="font-body text-sm">CNPJ *</Label>
            <Input value={form.cnpj} onChange={(e) => set("cnpj", e.target.value)} required placeholder="00.000.000/0001-00" data-testid={TID.registerCnpj} className="mt-1.5 bg-white" />
          </div>
          <div>
            <Label className="font-body text-sm">Celular (WhatsApp)</Label>
            <PhoneInput value={form.phone} onChange={(v) => set("phone", v)} data-testid={TID.registerPhone} className="mt-1.5" />
          </div>
          <div>
            <Label className="font-body text-sm">Plano</Label>
            <Select value={form.plan_slug} onValueChange={(v) => set("plan_slug", v)}>
              <SelectTrigger data-testid={TID.registerPlan} className="mt-1.5 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.slug}>
                    {p.name} · R$ {Number(p.monthly_price).toFixed(0)}/mês
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 mt-4 pt-4 border-t border-stone-200">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800 font-body mb-3">
              Administrador
            </div>
          </div>
          <div>
            <Label className="font-body text-sm">Nome completo *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid={TID.registerAdminName} className="mt-1.5 bg-white" />
          </div>
          <div>
            <Label className="font-body text-sm">E-mail *</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid={TID.registerEmail} className="mt-1.5 bg-white" />
          </div>
          <div className="sm:col-span-2">
            <Label className="font-body text-sm">Senha *</Label>
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} data-testid={TID.registerPassword} className="mt-1.5 bg-white" />
            <p className="mt-1 text-xs text-stone-500 font-body">Mínimo 6 caracteres.</p>
          </div>

          <div className="sm:col-span-2 mt-2 flex flex-col sm:flex-row gap-3 items-center justify-between">
            <Link to="/login" className="text-sm text-stone-600 hover:text-stone-900 font-body" data-testid={TID.registerGoToLogin}>
              Já tem conta? Entrar
            </Link>
            <Button
              type="submit"
              disabled={submitting}
              data-testid={TID.registerSubmit}
              className="bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-8 font-body"
            >
              {submitting ? "Criando..." : "Criar conta gratuita"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
